
import { CancelToken } from 'axios'

import { portalApi } from '@/axios'

import { Comment, Ticket } from '@/classes/models'

export default class TicketService {
  /**
   * Get tickets
   *
   * @param {Context} context
   * @param {TableRequest} parameters
   *
   * @return {Promise}
   */
  static getAll (context, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(this.endpoint(context), {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Map to Ticket
      response.data = response.data.map(ticket => new Ticket(ticket))

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Get a ticket by id.
   *
   * @param {Context} context
   * @param {string} ticketId
   *
   * @return {Promise}
   */
  static get (context, ticketId) {
    let request = portalApi.get(`${this.endpoint(context)}/${ticketId}`).then(response => {
      return response.data
    })

    return request.then(response => {
      // Map to Ticket
      return new Ticket(response.data)
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Create a ticket
   *
   * @param {Context} context
   * @param {Object} payload
   *
   * @return {Promise}
   */
  static add (context, payload) {
    return portalApi.post(this.endpoint(context), payload).then(response => {
      return response.data
    }).then(response => {
      // Map to Ticket
      return new Ticket(response.data)
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Update a ticket by id.
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {Object} payload
   *
   * @return {Promise}
   */
  static update (context, ticketId, payload) {
    return portalApi.put(`${this.endpoint(context)}/${ticketId}`, payload)
  }

  /**
   * Delete a ticket by id.
   *
   * @param {Context} context
   * @param {string} ticketId
   *
   * @return {Promise}
   */
  static delete (context, ticketId) {
    return portalApi.delete(`${this.endpoint(context)}/${ticketId}`)
  }

  /**
   * Download attachment by ticket attachment id
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {Object} ticketAttachmentId
   *
   * @return {Promise}
   */
  static getTicketAttachment (context, ticketId, ticketAttachmentId) {
    return portalApi.get(`${this.endpoint(context)}/${ticketId}/attachments/${ticketAttachmentId}`, { responseType: 'blob' })
  }

  /**
   * Add / upload an attachment to a ticket
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {Object} payload
   *
   * @return {Promise}
   */
  static addTicketAttachment (context, ticketId, payload) {
    return portalApi.post(`${this.endpoint(context)}/${ticketId}/attachments`, payload).then(response => {
      return response.data
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Delete a attachment by id.
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {Object} ticketAttachmentId
   *
   * @return {Promise}
   */
  static deleteTicketAttachment (context, ticketId, ticketAttachmentId) {
    return portalApi.delete(`${this.endpoint(context)}/${ticketId}/attachments/${ticketAttachmentId}`)
  }

  /**
   * Get ticket comments by ticket id
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {PaginationRequest} parameters
   *
   * @return {Promise}
   */
  static getTicketComments (context, ticketId, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint(context)}/${ticketId}/comments`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Map to Comment
      response.data = response.data.map(ticket => new Comment(ticket))

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Get a ticket comment by comment id.
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {string} ticketCommentId
   *
   * @return {Promise}
   */
  static getTicketComment (context, ticketId, ticketCommentId) {
    let request = portalApi.get(`${this.endpoint(context)}/${ticketId}/comments/${ticketCommentId}`).then(response => {
      return response.data
    })

    return request.then(response => {
      // Map to Comment
      return new Comment(response.data)
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Add a comment to a ticket
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {Object} payload
   *
   * @return {Promise}
   */
  static addTicketComment (context, ticketId, payload) {
    return portalApi.post(`${this.endpoint(context)}/${ticketId}/comments`, payload).then(response => {
      return response.data
    }).then(response => {
      // Map to Comment
      return new Comment(response.data)
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Update a ticket comment by ticket id and comment id.
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {string} ticketCommentId
   * @param {Object} payload
   *
   * @return {Promise}
   */
  static updateTicketComment (context, ticketId, ticketCommentId, payload) {
    return portalApi.put(`${this.endpoint(context)}/${ticketId}/comments/${ticketCommentId}`, payload)
  }

  /**
   * Delete a ticket comment by ticket id and comment id.
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {string} ticketCommentId
   *
   * @return {Promise}
   */
  static deleteTicketComment (context, ticketId, ticketCommentId) {
    return portalApi.delete(`${this.endpoint(context)}/${ticketId}/comments/${ticketCommentId}`)
  }

  /**
   * Download attachment by ticket attachment id
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {string} ticketCommentId
   * @param {Object} ticketAttachmentId
   *
   * @return {Promise}
   */
   static getTicketCommentAttachment (context, ticketId, ticketCommentId, ticketCommentAttachmentId) {
    return portalApi.get(`${this.endpoint(context)}/${ticketId}/comments/${ticketCommentId}/attachments/${ticketCommentAttachmentId}`, { responseType: 'blob' })
  }

  /**
   * Add / upload an attachment to a ticket comment
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {string} ticketCommentId
   * @param {Object} payload
   *
   * @return {Promise}
   */
  static addTicketCommentAttachment (context, ticketId, ticketCommentId, payload) {
    return portalApi.post(`${this.endpoint(context)}/${ticketId}/comments/${ticketCommentId}/attachments`, payload).then(response => {
      return response.data
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Delete a attachment by id.
   *
   * @param {Context} context
   * @param {string} ticketId
   * @param {string} ticketCommentId
   * @param {Object} ticketCommentAttachmentId
   *
   * @return {Promise}
   */
  static deleteTicketCommentAttachment (context, ticketId, ticketCommentId, ticketCommentAttachmentId) {
    return portalApi.delete(`${this.endpoint(context)}/${ticketId}/comments/${ticketCommentId}/attachments/${ticketCommentAttachmentId}`)
  }

  /**
   * Endpoint of API.
   *
   * @param {Context} context
   *
   * @return {string}
   */
  static endpoint (context) {
    return `${context.endpoint(['business', 'merchant'])}/tickets`
  }
}
